import React, { createContext, useState, useContext, useEffect } from 'react';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { API_BASE_URL } from '../API/api';
import authService from '../services/authService';
import { notifySessionExpired } from '../utils/axiosInterceptor';
import { openUsageSession, closeUsageSession, beaconCloseUsageSession } from '../utils/usageSession';

const FileContext = createContext();

export const useFileContext = () => useContext(FileContext);

export const FileProvider = ({ children }) => {
  const [filePaths, setFilePaths] = useState([]);
  const [isLasDividerEnabled, setIsLasDividerEnabled] = useState(false);
  const [isCarRemovalEnabled, setIsCarRemovalEnabled] = useState(false);
  const [isIntensityEnabled, setisIntensityEnabled] = useState(false);
  const [isSlopesCalcEnabled, setisSlopesCalcEnabled] = useState(false);
  const [isCSFEnabled, setIsCSFEnabled] = useState(false);
  const [isRoadLaneEnabled, setIsRoadLaneEnabled] = useState(false);
  const [isRoadDetectionEnabled, setIsRoadDetectionEnabled] = useState(false);
  const [isShouldersWidthCalcEnabled, setIsShouldersWidthCalcEnabled] = useState(false);
  const [isPSDClassificationEnabled , setIsPSDClassificationEnabled ] = useState(false);
  const [isAutomaticReportsEnabled , setIsAutomaticReportsEnabled ] = useState(false);
  const [auth, setAuthState] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [runLasDivider, setRunLasDivider] = useState(() => () => {});
  const [runSlopesCalc, setRunSlopesCalc] = useState(() => () => {});
  const [runCarRemoval, setRunCarRemoval] = useState(() => () => {});
  const [runRoadDetection, setRunRoadDetection] = useState(() => () => {});
  const [runCSF, setRunCSF] = useState(() => () => {});
  const [runRoadLane, setRunRoadLane] = useState(() => () => {});
  const [runShouldersWidthCalc , setRunShouldersWidthCalc ] = useState(() => () => {});
  const [runPSDClassification , setRunPSDClassification ] = useState(() => () => {});
  const [runAutomaticReports , setRunAutomaticReports ] = useState(() => () => {});
  const [runExistentGuardrails, setRunExistentGuardrails] = useState(null);

  useEffect(() => {
    // Synchronously rehydrate from storage to avoid redirect flicker
    try {
      const storedAuth = sessionStorage.getItem('auth');
      const storedEmail = sessionStorage.getItem('userEmail');
      let initialAuth = false;
      if (storedAuth === 'true') {
        initialAuth = true;
      } else if (storedAuth) {
        try { initialAuth = JSON.parse(storedAuth) === true; } catch (e) {}
      }
      if (initialAuth) {
        setAuthState(true);
        if (storedEmail) setUserEmail(storedEmail);
      }
    } catch (e) {}

    // Then verify asynchronously (Amplify or fallback token)
    checkAuthStatus();

    // Close the usage session on browser unload (best-effort beacon). Fires on
    // refresh/navigation too, so the backend reattaches a just-closed session
    // when the user is only refreshing (see usageSession / usage_routes).
    const handleUnload = () => beaconCloseUsageSession('browser_closed');
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Helper function to set cookie for pixel streaming authentication
  const setAuthCookie = (token) => {
    try {
      // Set cookie for root domain (.123bimlive.com) - accessible to both www and non-www
      const expires = new Date();
      expires.setHours(expires.getHours() + 1); // 1 hour expiration (matches token TTL)
      
      document.cookie = `cognito_token=${token}; expires=${expires.toUTCString()}; domain=.123bimlive.com; path=/; Secure; SameSite=None`;
      console.log('✅ Cookie set for pixel streaming authentication');
    } catch (error) {
      console.error('❌ Error setting auth cookie:', error);
    }
  };

  // Helper function to clear auth cookie
  const clearAuthCookie = () => {
    try {
      document.cookie = 'cognito_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.123bimlive.com; path=/;';
      console.log('✅ Auth cookie cleared');
    } catch (error) {
      console.error('❌ Error clearing auth cookie:', error);
    }
  };

  const checkAuthStatus = async () => {
    console.log('🔍 FileContext - checkAuthStatus called, current URL:', window.location.pathname);
    try {
      // Check if user is authenticated with AWS Cognito
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken) {
        throw new Error('No Cognito session');
      }
      console.log('🔍 FileContext - User is authenticated');

      // Set cookie with Cognito token (accessible to 123bimlive.com)
      setAuthCookie(tokens.idToken.toString());

      // If we get here, user is authenticated
      console.log('🔍 FileContext - Setting auth state to true, current URL:', window.location.pathname);
      setAuthState(true);
      const email = tokens.idToken.payload.email;
      if (email) {
        setUserEmail(email);
        sessionStorage.setItem('userEmail', email);
      }
      sessionStorage.setItem('auth', 'true');
      setAuthLoading(false);
      console.log('🔍 FileContext - Auth state set to true, current URL:', window.location.pathname);

      // Open (or reattach to) the usage login session — best-effort.
      openUsageSession();

    } catch (error) {
      console.log('🔍 FileContext - Auth check error:', error);
      console.log('🔍 FileContext - Error details:', {
        code: error?.code,
        message: error?.message,
        name: error?.name,
        log: error?.log
      });
      
      // Check for network/transient errors (should not clear cookie on these)
      const isTransientError = 
        error?.code === 'NetworkError' ||
        error?.message?.includes('Network request failed') ||
        error?.message?.includes('timeout') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('network') ||
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ETIMEDOUT';
      
      // Check if this is a permanent authentication failure
      // Only clear cookie for confirmed authentication errors, not network/transient errors
      const isPermanentAuthError = 
        (!isTransientError) && (
          error?.code === 'NotAuthorizedException' ||
          error?.message?.includes('not authenticated') ||
          error?.message?.includes('User is not authenticated') ||
          error?.message?.includes('The user is not authenticated') ||
          error?.log?.includes('not authenticated') ||
          // 400 Bad Request from Cognito typically means refresh token is invalid/expired
          (error?.response?.status === 400 && error?.response?.config?.url?.includes('cognito-idp'))
        );
      
      // Only clear cookie on confirmed permanent auth failures, preserve for transient errors
      if (isPermanentAuthError) {
        console.log('🔍 FileContext - Permanent auth error detected, clearing cookie');
        clearAuthCookie();
      } else if (isTransientError) {
        console.log('🔍 FileContext - Transient error detected, preserving cookie for retry');
        // Don't clear cookie on transient errors - user might still be authenticated
      } else {
        // For unknown errors, be conservative - don't clear cookie unless we're certain it's an auth failure
        console.log('🔍 FileContext - Unknown error type, preserving cookie (conservative approach)');
        // Only clear cookie if we're very certain it's an auth failure
        if (error?.message?.toLowerCase().includes('auth') || error?.code?.toLowerCase().includes('auth')) {
          console.log('🔍 FileContext - Error appears auth-related, clearing cookie');
          clearAuthCookie();
        }
      }
      
      // Fallback: accept existing session token (custom login)
      try {
        const token = sessionStorage.getItem('token');
        if (token) {
          console.log('🔍 FileContext - Using fallback token, current URL:', window.location.pathname);
          setAuthState(true);
          const storedEmail = sessionStorage.getItem('userEmail');
          if (storedEmail) setUserEmail(storedEmail);
          sessionStorage.setItem('auth', 'true');
          setAuthLoading(false);
          console.log('🔍 FileContext - Fallback auth set to true, current URL:', window.location.pathname);
          return;
        }
      } catch (e) {}

      // No valid session found
      setAuthState(false);
      setUserEmail('');
      sessionStorage.removeItem('auth');
      sessionStorage.removeItem('userEmail');
      setAuthLoading(false);
      
      // Check if we have a token but it's expired/invalid
      // This will trigger the session expired modal via axios interceptor
      const token = sessionStorage.getItem('token') || 
                    sessionStorage.getItem('bimLiveAuthToken') ||
                    sessionStorage.getItem('cognito_token') ||
                    sessionStorage.getItem('idToken');
      
      if (token) {
        // Token exists but auth failed - likely expired
        // The axios interceptor will catch this on the next API call
        // For now, we'll just clear the invalid token
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('bimLiveAuthToken');
        sessionStorage.removeItem('cognito_token');
        sessionStorage.removeItem('idToken');
      }
    }
  };

  const setAuth = (authState, email = '') => {
    setAuthState(authState);
    setUserEmail(email);
    sessionStorage.setItem('auth', JSON.stringify(authState));
    if (email) {
      sessionStorage.setItem('userEmail', email);
    }
    // Open the usage login session on a fresh login (the provider is already
    // mounted, so checkAuthStatus won't re-run). Idempotent + best-effort.
    if (authState) {
      openUsageSession();
    }
  };

  const logout = async () => {
    // Redirect to the homepage on the current origin (origin-agnostic across dev/prod).
    const redirectUrl = `${window.location.origin}/`;

    // Close the usage login session BEFORE Auth.signOut (needs the idToken to
    // authenticate the close). Global sign-out ends all devices, so the backend
    // closes every open session for this user with reason explicit_logout.
    try {
      await closeUsageSession('explicit_logout');
    } catch (e) {
      console.log('Error closing usage session:', e);
    }

    try {
      // Clear auth cookie for pixel streaming (cookie-based auth doesn't need session revocation)
      clearAuthCookie();

      // Sign out from AWS Cognito with global flag to clear all sessions
      await signOut({ global: true });
      console.log('User signed out from Cognito globally');
    } catch (error) {
      console.log('Error signing out:', error);
    }

    // Clear all local state first so UI updates
    setAuthState(false);
    setUserEmail('');
    setAuthLoading(false);

    try {
      // Clear all session storage
      sessionStorage.clear();
      // Clear all local storage
      localStorage.clear();
    } catch (e) {
      console.log('Error clearing storage:', e);
    }

    // v6 signOut above already clears any cached tokens.

    try {
      // Clear any cookies (if any) - including the cognito_token cookie
      document.cookie.split(";").forEach(function(c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      clearAuthCookie();
    } catch (e) {
      console.log('Error clearing cookies:', e);
    }

    console.log('Complete logout - all tokens and data cleared');

    // Always redirect to main page (use replace so back button doesn't return to /projects)
    // Defer so it runs after any synchronous React updates and can't be skipped by errors above
    setTimeout(() => {
      window.location.replace(redirectUrl);
    }, 0);
  };

  // Function to refresh auth status (useful for debugging)
  const refreshAuthStatus = () => {
    checkAuthStatus();
  };

  // Function to force refresh JWT token (useful when permissions change)
  const forceRefreshToken = async () => {
    try {
      console.log('Forcing JWT token refresh...');

      const refreshedSession = await fetchAuthSession({ forceRefresh: true });

      console.log('JWT token refreshed successfully');
      return refreshedSession;
    } catch (error) {
      console.log('Error refreshing token:', error);
      notifySessionExpired('forceRefreshToken failed', { error: error?.message });
      await logout();
      throw error;
    }
  };

  // Make functions available globally for debugging
  window.refreshAuth = refreshAuthStatus;
  window.forceRefreshToken = forceRefreshToken;

  // Function to verify token and get user privileges
  const verifyTokenAndPrivileges = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken) {
        throw new Error('No Cognito session');
      }
      const accessToken = tokens.accessToken?.toString();
      const idToken = tokens.idToken.toString();
      const idTokenPayload = tokens.idToken.payload;

      // Groups are in the idToken, not accessToken
      const groups = idTokenPayload['cognito:groups'] || [];

      console.log('ID Token payload:', idTokenPayload);
      console.log('User groups:', groups);
      console.log('User email:', idTokenPayload.email);
      console.log('Token expires:', new Date(idTokenPayload.exp * 1000));

      return {
        isValid: true,
        user: { username: idTokenPayload['cognito:username'], attributes: { email: idTokenPayload.email } },
        token: accessToken, // Keep accessToken for API calls
        idToken: idToken, // Also return idToken
        email: idTokenPayload.email,
        groups: groups,
        isAdmin: groups.includes('Admin'),
        isManager: groups.includes('Manager'),
        expiresAt: new Date(idTokenPayload.exp * 1000)
      };
    } catch (error) {
      console.log('Token verification failed:', error);
      notifySessionExpired('verifyTokenAndPrivileges failed', { error: error?.message });
      return {
        isValid: false,
        error: error.message
      };
    }
  };

  // Make token verification available globally
  window.verifyToken = verifyTokenAndPrivileges;

  // Function to get user's UI permissions (what they can see/do)
  const getUserUIPermissions = async () => {
    const accessLevel = await getUserAccessLevel();
    
    return {
      // Navigation items they can see
      canSeeAdminPanel: accessLevel.permissions.includes('access_admin_panel'),
      canSeeManagementPanel: accessLevel.permissions.includes('access_management_panel'),
      canSeeUserManagement: accessLevel.permissions.includes('manage_users'),
      
      // Actions they can perform
      canDeleteUsers: accessLevel.permissions.includes('delete_users'),
      canAssignAdmin: accessLevel.permissions.includes('assign_admin'),
      canManageSystemSettings: accessLevel.permissions.includes('manage_system_settings'),
      canViewAllData: accessLevel.permissions.includes('view_all_data'),
      canUploadFiles: accessLevel.permissions.includes('upload_files'),
      
      // Access level for conditional rendering
      isAdmin: accessLevel.level >= 3,
      isManager: accessLevel.level >= 2,
      isUser: accessLevel.level >= 1,
      
      // Raw data
      level: accessLevel.level,
      groups: accessLevel.groups,
      permissions: accessLevel.permissions
    };
  };

  // Make UI permissions available globally
  window.getUserUIPermissions = getUserUIPermissions;

  // Define group permissions and access levels.
  // UI-visibility copy of the server-side map in Backend/123bimlive/routes/auth_routes.py
  // — keep the two in sync. Enforcement always happens server-side (@require_auth);
  // this copy only decides what to render.
  const SUPER_USER_PERMISSIONS = [
    'manage_customer_users',
    'assign_project_users',
    'delete_customer_resources',
    'view_usage_assigned',
    'create_usage_reports',
    'schedule_usage_reports',
    'manage_overviews',
    'view_own_data',
    'upload_files'
  ];

  const GROUP_PERMISSIONS = {
    'Admin': {
      level: 3,
      permissions: [
        ...SUPER_USER_PERMISSIONS,
        'manage_users',
        'delete_users',
        'assign_admin',
        'view_all_data',
        'manage_system_settings',
        'access_admin_panel',
        'view_all_projects',
        'view_usage_all',
        'view_server_details',
        'view_costs',
        'create_cost_reports',
        'schedule_cost_reports',
        'view_safety_dashboard'
      ],
      description: 'Full system access'
    },
    'SuperUser': {
      level: 2,
      permissions: [...SUPER_USER_PERMISSIONS],
      description: 'Customer-side administrator'
    },
    'Manager': {
      level: 2,
      permissions: [
        'manage_users',
        'view_all_data',
        'access_management_panel'
      ],
      description: 'Management access'
    },
    'User': {
      level: 1,
      permissions: [
        'view_own_data',
        'upload_files'
      ],
      description: 'Basic user access'
    }
  };

  // Function to check specific permission
  const hasPermission = async (permission) => {
    const tokenInfo = await verifyTokenAndPrivileges();
    if (!tokenInfo.isValid) {
      return { hasPermission: false, reason: 'Invalid token' };
    }
    
    // Check if user has the specific permission
    for (const group of tokenInfo.groups) {
      if (GROUP_PERMISSIONS[group] && GROUP_PERMISSIONS[group].permissions.includes(permission)) {
        return { hasPermission: true, group: group, level: GROUP_PERMISSIONS[group].level };
      }
    }
    
    return { hasPermission: false, reason: 'Permission not granted' };
  };

  // Function to check if current user can manage other users
  const canManageUsers = async () => {
    return await hasPermission('manage_users');
  };

  // Function to check if user can delete other users
  const canDeleteUsers = async () => {
    return await hasPermission('delete_users');
  };

  // Function to check if user can assign admin privileges
  const canAssignAdmin = async () => {
    return await hasPermission('assign_admin');
  };

  // Function to get user's access level
  const getUserAccessLevel = async () => {
    const tokenInfo = await verifyTokenAndPrivileges();
    if (!tokenInfo.isValid) {
      return { level: 0, groups: [], permissions: [] };
    }
    
    let maxLevel = 0;
    let allPermissions = [];
    
    for (const group of tokenInfo.groups) {
      if (GROUP_PERMISSIONS[group]) {
        maxLevel = Math.max(maxLevel, GROUP_PERMISSIONS[group].level);
        allPermissions = [...allPermissions, ...GROUP_PERMISSIONS[group].permissions];
      }
    }
    
    return {
      level: maxLevel,
      groups: tokenInfo.groups,
      permissions: [...new Set(allPermissions)] // Remove duplicates
    };
  };

  // NOTE: the backend verifies the Cognito **idToken** (JWKS-signed); the
  // accessToken fails that check, so all admin calls below send tokenInfo.idToken.
  // Group assignment (Make Admin / Remove Admin / update-user-groups) was removed:
  // Administrator membership is managed manually in the Cognito console.

  // Function to get user list (requires admin/manager privileges)
  const getUserList = async () => {
    const permissionCheck = await canManageUsers();
    if (!permissionCheck.hasPermission) {
      throw new Error(`Access denied: ${permissionCheck.reason}`);
    }

    try {
      const tokenInfo = await verifyTokenAndPrivileges();
      const response = await fetch('/api/admin/list-users', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenInfo.idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user list:', error);
      throw error;
    }
  };

  // Function to remove user
  const removeUser = async (username) => {
    const permissionCheck = await canDeleteUsers();
    if (!permissionCheck.hasPermission) {
      throw new Error(`Access denied: ${permissionCheck.reason}`);
    }

    try {
      const tokenInfo = await verifyTokenAndPrivileges();
      const response = await fetch('/api/admin/remove-user', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenInfo.idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username: username })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove user: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing user:', error);
      throw error;
    }
  };

  // Disable / re-enable a user (Cognito admin_disable_user + app-level flag)
  const setUserEnabled = async (username, enabled) => {
    const permissionCheck = await canManageUsers();
    if (!permissionCheck.hasPermission) {
      throw new Error(`Access denied: ${permissionCheck.reason}`);
    }

    const tokenInfo = await verifyTokenAndPrivileges();
    const endpoint = enabled ? '/api/admin/enable-user' : '/api/admin/disable-user';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${tokenInfo.idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username: username })
    });

    if (!response.ok) {
      throw new Error(`Failed to ${enabled ? 'enable' : 'disable'} user: ${response.statusText}`);
    }

    return await response.json();
  };

  // Make user management functions available globally
  window.canManageUsers = canManageUsers;
  window.canDeleteUsers = canDeleteUsers;
  window.canAssignAdmin = canAssignAdmin;
  window.hasPermission = hasPermission;
  window.getUserAccessLevel = getUserAccessLevel;
  window.getUserList = getUserList;
  window.removeUser = removeUser;
  window.setUserEnabled = setUserEnabled;

  return (
    <FileContext.Provider
      value={{
        filePaths,
        setFilePaths,
        isLasDividerEnabled,
        setIsLasDividerEnabled,
        isCarRemovalEnabled,
        setIsCarRemovalEnabled,
        isIntensityEnabled,
        setisIntensityEnabled,
        isSlopesCalcEnabled,
        setisSlopesCalcEnabled,
        isCSFEnabled,
        setIsCSFEnabled,
        isRoadLaneEnabled,
        setIsRoadLaneEnabled,
        isRoadDetectionEnabled,
        setIsRoadDetectionEnabled,
        isShouldersWidthCalcEnabled,
        setIsShouldersWidthCalcEnabled,
        isPSDClassificationEnabled,
        setIsPSDClassificationEnabled,
        isAutomaticReportsEnabled,
        setIsAutomaticReportsEnabled,
        auth,
        authLoading,
        setAuth,
        userEmail,
        logout,
        refreshAuthStatus,
        forceRefreshToken,
        verifyTokenAndPrivileges,
        canManageUsers,
        canDeleteUsers,
        canAssignAdmin,
        hasPermission,
        getUserAccessLevel,
        getUserList,
        removeUser,
        setUserEnabled,
        runLasDivider,
        setRunLasDivider,
        runSlopesCalc,
        setRunSlopesCalc,
        runCSF,
        setRunCSF,
        runRoadLane,
        setRunRoadLane,
        runCarRemoval,
        setRunCarRemoval,
        runRoadDetection,
        setRunRoadDetection,
        runShouldersWidthCalc,
        setRunShouldersWidthCalc,
        runPSDClassification,
        setRunPSDClassification,
        runAutomaticReports,
        setRunAutomaticReports, 
        runExistentGuardrails,
        setRunExistentGuardrails,
      }}>
      {children}
    </FileContext.Provider>
  );
};
